import PanelCard from 'components/card/PanelCard';
import PostIt from 'components/postIt/PostIt';
import RemunerationConditionsCard from 'components/remunerationSummary/RemunerationConditionsCard';
import { PanelCardSkeleton } from 'components/skeleton/Skeleton';
import { formatBonusValue } from 'helper/bonusValue';

const DASH = '—';

/**
 * What the invoice was billed against (JBWEB-1274): the company's post-it,
 * its remuneration conditions and its bonuses. The conditions and bonuses are
 * read-only, their editors living on the company panel; the post-it is edited
 * from any copy of it (JBWEB-1360), so it opens its editor here too and hands
 * the saved note to `onPostItSaved`. `company` is what `admin/getCompanyInfo`
 * answers, null while read; `failed` says the read did not succeed.
 */
const InvoiceCompanyTerms = ({
  company,
  failed,
  companyName,
  text,
  onPostItSaved,
}) => {
  const terms = (key, fallback) => text(`companyTerms.${key}`, fallback);

  if (failed) {
    return (
      <div role="alert" className="text-sm text-gray-500">
        {terms(
          'loadFailed',
          'Les conditions de l’entreprise n’ont pas pu être chargées.',
        )}
      </div>
    );
  }
  // While it is read, the two cards' frames and titles hold their place: the
  // panel no longer jumps when they arrive.
  if (!company) {
    return (
      <>
        <PanelCardSkeleton title="Conditions de rémunération" rows={4} />
        <PanelCardSkeleton title={terms('bonusesTitle', 'Primes & indemnités')} rows={2} />
      </>
    );
  }

  const bonuses = company.bonusesAndAllowances?.bonuses ?? [];

  return (
    <>
      <PostIt
        note={company.post_it}
        companyName={companyName}
        companyId={company._id}
        onSaved={onPostItSaved}
      />
      <RemunerationConditionsCard
        companyInfo={company}
        contentClassName="text-sm"
      />
      <PanelCard
        title={terms('bonusesTitle', 'Primes & indemnités')}
        collapsible
      >
        {bonuses.length === 0 ? (
          <div className="py-3 px-4 text-sm text-gray-500">
            {terms('noBonuses', 'Aucune prime ni indemnité.')}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left uppercase text-gray-500 border-b border-gray-100">
                <th className="px-4 py-3 font-normal">
                  {terms('bonusName', 'Nom')}
                </th>
                <th className="px-4 py-3 font-normal">
                  {terms('bonusUnit', 'Unité')}
                </th>
                <th className="px-4 py-3 font-normal">
                  {terms('bonusRate', 'Valeur')}
                </th>
              </tr>
            </thead>
            <tbody>
              {bonuses.map((bonus, index) => (
                <tr
                  key={bonus._id ?? index}
                  className="border-b border-gray-100 last:border-b-0 text-gray-900"
                >
                  <td className="px-4 py-3">{bonus.name || DASH}</td>
                  <td className="px-4 py-3">{bonus.unit || DASH}</td>
                  <td className="px-4 py-3">{formatBonusValue(bonus)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </PanelCard>
    </>
  );
};

export default InvoiceCompanyTerms;
